"use client";

import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Link } from "@heroui/link";
import { Alert02Icon, Mail01Icon } from "@icons";
import { type FormEvent, useState } from "react";
import { InviteShell } from "@/features/organizations/components/InviteShell";
import { extractApiError } from "@/lib/api/extractError";
import { authApi } from "../api/authApi";

export function ForgotPasswordForm() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.requestPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(
        extractApiError(err, "Could not send the reset email. Try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <InviteShell>
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex size-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Mail01Icon className="size-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">
            Check your email
          </h1>
          <p className="text-sm text-default-500">
            {"If an account exists for "}
            <span className="text-foreground">{email.trim()}</span>
            {", we've sent it a link to reset the password."}
          </p>
          <Button as={Link} href="/login" className="mt-3" variant="flat">
            Back to sign in
          </Button>
        </div>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <h1 className="mb-1 text-xl font-semibold text-foreground">
        Reset your password
      </h1>
      <p className="mb-5 text-sm text-default-500">
        {"Enter your account email and we'll send you a reset link."}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <Input
          type="email"
          label="Email"
          labelPlacement="outside"
          placeholder="you@company.com"
          autoComplete="email"
          value={email}
          onValueChange={setEmail}
          isDisabled={submitting}
        />

        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-danger/10 px-3 py-2 text-sm text-danger">
            <Alert02Icon className="mt-0.5 size-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <Button
          type="submit"
          color="primary"
          className="mt-1 font-medium"
          isLoading={submitting}
          isDisabled={!email.trim() || submitting}
        >
          Send reset link
        </Button>
      </form>

      <p className="mt-5 text-center text-sm text-default-500">
        {"Remembered it? "}
        <Link href="/login" size="sm" className="text-primary">
          Sign in
        </Link>
      </p>
    </InviteShell>
  );
}
